import type {Key} from 'react-aria-components'
import {
  ComboBox,
  FieldError,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
} from 'react-aria-components'

import {
  isStateProvinceRequiredCountry,
  type StateProvinceRequiredCountries,
} from '@/lib/flight/constants'
import {isStateProvinceOf, REGIONS_MAP} from '@/lib/locale/regions'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './StateProvinceSelector.module.scss'

interface StateProvinceSelectorProps {
  value: string
  onChange: (value: string) => void
  countryCode: StateProvinceRequiredCountries
  isRequired?: boolean
  isInvalid?: boolean
  errorMessage?: string
  name?: string
  onBlur?: () => void
  inputRef?: (instance: HTMLInputElement | null) => void
  placeholder?: string
  inputClassName?: string
  label?: string
}

export default function StateProvinceSelector({
  value,
  onChange,
  countryCode,
  isRequired = false,
  isInvalid = false,
  errorMessage,
  name,
  onBlur,
  inputRef,
  placeholder,
  inputClassName,
  label,
}: StateProvinceSelectorProps) {
  const [inputValue, setInputValue] = useState('')

  const regions = useMemo(() => {
    if (isStateProvinceRequiredCountry(countryCode))
      return Object.entries(REGIONS_MAP[countryCode]).map(([code, name]) => ({
        code,
        name,
      }))
    return []
  }, [countryCode])

  const checkAndSetExactMatch = (val: string) => {
    const upperCaseVal = val.toUpperCase()
    if (isStateProvinceOf(countryCode, upperCaseVal)) {
      setInputValue(REGIONS_MAP[countryCode][upperCaseVal])
      onChange(upperCaseVal)
      return true
    }
    return false
  }

  const filteredRegions = useMemo(() => {
    if (!inputValue) return regions
    const searchTerm = inputValue.toLowerCase()
    return regions.filter(r => r.name.toLowerCase().includes(searchTerm))
  }, [inputValue, regions])

  useEffect(() => {
    if (
      !isStateProvinceRequiredCountry(countryCode) ||
      (value && !regions.find(r => r.code === value))
    ) {
      // if the country code has been changed and the value is not in the regions array, clear the input value and onChange
      setInputValue('')
      onChange('')
    }
  }, [countryCode, onChange, value, regions])

  useEffect(() => {
    const region = regions.find(r => r.code === value)
    if (region) {
      setInputValue(region.name)
    }
  }, [value, regions])

  const handleSelectionChange = (key: Key | null) => {
    const region = regions.find(r => r.code === key)
    if (region) {
      setInputValue(region.name)
      onChange(region.code)
    } else {
      setInputValue('')
      onChange('')
    }
  }

  const handleInputChange = (val: string) => {
    setInputValue(val)
    if (!val.trim()) {
      onChange('')
    } else {
      checkAndSetExactMatch(val)
    }
  }

  const handleBlur = () => {
    if (!value && inputValue) {
      if (!checkAndSetExactMatch(inputValue)) {
        setInputValue('')
      }
    }
    onBlur?.()
  }

  const shouldShowError = isInvalid

  return (
    <ComboBox
      className={style.ComboBox}
      selectedKey={value || null}
      onSelectionChange={handleSelectionChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onBlur={handleBlur}
      isRequired={isRequired}
      isInvalid={shouldShowError}
      menuTrigger='focus'
      shouldFocusWrap
      items={filteredRegions}
    >
      <Label className={style.Label} id='state-province-label'>
        {label ?? 'State / Province of residence'}
      </Label>

      <VisuallyHidden id='state-instructions'>
        Start typing or press down arrow to open the list of states or provinces.
      </VisuallyHidden>

      <div className={style.InputWrapper}>
        <Input
          className={inputClassName ?? style.Input}
          autoComplete='off'
          aria-labelledby='state-province-label'
          aria-describedby='state-instructions'
          name={name}
          ref={inputRef}
          placeholder={placeholder ?? ''}
        />

        <Popover className={style.Popover} placement='bottom start' isNonModal shouldFlip>
          <ListBox className={style.ListBox}>
            {filteredRegions.map(region => (
              <ListBoxItem
                key={region.code}
                id={region.code}
                className={style.ListBoxItem}
                textValue={region.name}
              >
                {region.name}
              </ListBoxItem>
            ))}
          </ListBox>
        </Popover>
      </div>
      {shouldShowError && (
        <FieldError className={style.Error}>
          {errorMessage ?? 'State / Province is required for US / Canada'}
        </FieldError>
      )}
    </ComboBox>
  )
}
